import { useCallback, useEffect, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import ScriptEditorToolbar from "./scriptEditor/ScriptEditorToolbar.jsx";
import { DEFAULT_SCRIPT } from "./scriptEditor/constants.js";
import { formatAnimationScript } from "./scriptEditor/utils/editorTextUtils.js";
import useScriptVersions from "../../../hooks/useScriptVersions.js";
import useScriptExecution from "../../../hooks/useScriptExecution.js";
import useSnippets from "../../../hooks/useSnippets.js";
import { getAnimations } from "../../../services/animation.service.js";
import {
  getScript as fetchScript,
  saveScriptDraft,
} from "../../../services/script.service.js";
import BUILT_IN_COMMANDS from "../../../script/BUILT_IN_COMMANDS.js";
import SPECIAL_FUNCTIONS from "../../../script/SPECIAL_FUNCTIONS.js";
import Console from "./Console.jsx";
import VisualSummaryPanel from "./VisualSummaryPanel.jsx";

const LANGUAGE_ID = "animation-script";
const MIN_CONSOLE_HEIGHT = 120;
const MIN_EDITOR_HEIGHT = 10;
const SPLITTER_SIZE = 6;
const DRAFT_AUTOSAVE_DELAY_MS = 2000;
const RECENT_EDITOR_VALUE_QUEUE_LIMIT = 20;

const TYPES = ["number", "string", "bool", "void", "component", "array"];
const KEYWORDS = [
  "time",
  "if",
  "else",
  "for",
  "while",
  "function",
  "return",
  "break",
  "continue",
  "true",
  "false",
  "audio",
  "import",
  "class",
  "extends",
  "this",
  "super",
  "public",
  "private",
  "protected"
];
const COMMANDS = Object.keys(BUILT_IN_COMMANDS);
const SPECIAL_FNS = Object.keys(SPECIAL_FUNCTIONS);

const splitTopLevel = (text, delimiter = ",") => {
  const parts = [];
  let current = "";
  let quote = null;
  let escaped = false;
  let parenDepth = 0;
  let bracketDepth = 0;
  let braceDepth = 0;

  for (let i = 0; i < String(text || "").length; i += 1) {
    const char = text[i];

    if (quote) {
      current += char;
      if (escaped) {
        escaped = false;
        continue;
      }
      if (char === "\\") {
        escaped = true;
        continue;
      }
      if (char === quote) {
        quote = null;
      }
      continue;
    }

    if (char === "'" || char === '"' || char === "`") {
      quote = char;
      current += char;
      continue;
    }

    if (char === "(") parenDepth += 1;
    else if (char === ")") parenDepth = Math.max(0, parenDepth - 1);
    else if (char === "[") bracketDepth += 1;
    else if (char === "]") bracketDepth = Math.max(0, bracketDepth - 1);
    else if (char === "{") braceDepth += 1;
    else if (char === "}") braceDepth = Math.max(0, braceDepth - 1);

    if (
      char === delimiter &&
      parenDepth === 0 &&
      bracketDepth === 0 &&
      braceDepth === 0
    ) {
      parts.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  parts.push(current);
  return parts;
};

const extractDeclaredName = (rawPart) => {
  const candidate = String(rawPart || "")
    .split("=")[0]
    ?.trim();
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(candidate || "") ? candidate : null;
};

const getAnimationLookupName = (record) =>
  String(
    record?.title ??
      record?.name ??
      record?.metadata?.title ??
      record?.metadata?.name ??
      "",
  ).trim();

const getAnimationLookupId = (record) =>
  record?.animationId ?? record?.metadata?.animationId ?? null;

const getScriptText = (scriptRecord) =>
  String(
    scriptRecord?.text ??
      scriptRecord?.scriptText ??
      scriptRecord?.script?.text ??
      scriptRecord?.script ??
      "",
  );

const findImportedAnimationMatch = (records, importName) => {
  const normalizedImportName = String(importName || "").trim().toLowerCase();
  if (!normalizedImportName) return null;

  const exactMatch = (records || []).find(
    (record) =>
      getAnimationLookupName(record).toLowerCase() === normalizedImportName,
  );
  if (exactMatch) return exactMatch;

  return (records || []).find((record) =>
    getAnimationLookupName(record).toLowerCase().startsWith(normalizedImportName),
  );
};

const parseParameterList = (paramsRaw) => {
  const params = [];

  splitTopLevel(paramsRaw)
    .forEach((param) => {
      const trimmed = param.trim();
      if (!trimmed) return;
      const typedMatch = trimmed.match(
        /^(number|string|bool|component)\|\s*([A-Za-z_][A-Za-z0-9_]*)/,
      );
      const arrayMatch = trimmed.match(
        /^array\|\s*(number|string|bool|component)\|\s*([A-Za-z_][A-Za-z0-9_]*)/,
      );
      if (arrayMatch) {
        params.push({ type: `array|${arrayMatch[1]}`, name: arrayMatch[2] });
      } else if (typedMatch) {
        params.push({ type: typedMatch[1], name: typedMatch[2] });
      } else {
        const [lhs] = trimmed.split("=");
        const clean = lhs?.trim();
        if (clean) params.push({ type: "any", name: clean });
      }
    });

  return params;
};

const extractDeclaredFunctions = (text) => {
  const functions = new Map();
  const classRanges = Array.from(extractClassDefinitions(text).values()).map(
    (def) => ({
      start: def.rangeStart,
      end: def.rangeEnd,
    }),
  );
  const functionDeclRegex =
    /\b(?:(?:public|private|protected)\s+)?function\|\s*(number|string|bool|void)\s*\|\s*([A-Za-z_][A-Za-z0-9_]*)\s*\(([^)]*)\)/g;
  let match;

  while ((match = functionDeclRegex.exec(text)) !== null) {
    const isClassMember = classRanges.some(
      (range) => match.index > range.start && match.index < range.end,
    );
    if (isClassMember) {
      continue;
    }

    const returnType = match[1];
    const name = match[2];
    const params = parseParameterList(match[3] || "");

    functions.set(name, { name, returnType, params, source: "user" });
  }

  return functions;
};

const buildFunctionSignatureMap = (text) => {
  const map = new Map();

  Object.entries(SPECIAL_FUNCTIONS).forEach(([name, def]) => {
    map.set(name, {
      name,
      returnType: def.returnType || "any",
      params: Array.isArray(def.params) ? def.params : [],
      source: "builtin",
    });
  });

  for (const [name, def] of extractDeclaredFunctions(text).entries()) {
    map.set(name, def);
  }

  return map;
};

const getCodeMask = (text) => {
  const isCode = new Array(text.length).fill(true);
  let i = 0;

  while (i < text.length) {
    const ch = text[i];
    const next = text[i + 1];

    if (ch === "/" && next === "/") {
      isCode[i] = false;
      isCode[i + 1] = false;
      i += 2;
      while (i < text.length && text[i] !== "\n") {
        isCode[i] = false;
        i += 1;
      }
      continue;
    }

    if (ch === "/" && next === "*") {
      isCode[i] = false;
      isCode[i + 1] = false;
      i += 2;
      while (i < text.length) {
        isCode[i] = false;
        if (text[i] === "*" && text[i + 1] === "/") {
          isCode[i + 1] = false;
          i += 2;
          break;
        }
        i += 1;
      }
      continue;
    }

    if (ch === "'" || ch === '"' || ch === "`") {
      const quote = ch;
      isCode[i] = false;
      i += 1;
      let escaped = false;
      while (i < text.length) {
        isCode[i] = false;
        if (escaped) {
          escaped = false;
          i += 1;
          continue;
        }
        if (text[i] === "\\") {
          escaped = true;
          i += 1;
          continue;
        }
        if (text[i] === quote) {
          i += 1;
          break;
        }
        i += 1;
      }
      continue;
    }

    i += 1;
  }

  return isCode;
};

const parseImportNames = (text) => {
  const imports = new Set();
  const codeMask = getCodeMask(text);
  const importRegex = /\bimport\s+([A-Za-z_][A-Za-z0-9_]*)\s*;/g;
  let match;

  while ((match = importRegex.exec(text)) !== null) {
    let isCodeImport = true;
    for (let i = match.index; i < importRegex.lastIndex; i += 1) {
      if (!codeMask[i]) {
        isCodeImport = false;
        break;
      }
    }

    if (isCodeImport) {
      imports.add(match[1]);
    }
  }

  return Array.from(imports.values());
};

const getCallContext = (text, offset) => {
  const safeOffset = Math.max(0, Math.min(text.length, offset));
  if (!text || safeOffset === 0) return null;
  const codeMask = getCodeMask(text);

  let depth = 0;
  let openIndex = -1;
  for (let i = safeOffset - 1; i >= 0; i -= 1) {
    if (!codeMask[i]) continue;
    const ch = text[i];
    if (ch === ")") {
      depth += 1;
    } else if (ch === "(") {
      if (depth === 0) {
        openIndex = i;
        break;
      }
      depth -= 1;
    }
  }

  if (openIndex < 0) return null;

  let nameEnd = openIndex - 1;
  while (nameEnd >= 0 && /\s/.test(text[nameEnd])) nameEnd -= 1;
  let nameStart = nameEnd;
  while (nameStart >= 0 && /[A-Za-z0-9_]/.test(text[nameStart])) {
    nameStart -= 1;
  }
  nameStart += 1;
  if (nameStart > nameEnd) return null;
  const name = text.slice(nameStart, nameEnd + 1);
  if (!name) return null;

  const lineStart = text.lastIndexOf("\n", nameStart) + 1;
  const lineBefore = text.slice(lineStart, nameStart);
  if (lineBefore.includes("function|")) {
    return null;
  }

  let argIndex = 0;
  let innerDepth = 0;
  for (let i = openIndex + 1; i < safeOffset; i += 1) {
    if (!codeMask[i]) continue;
    const ch = text[i];
    if (ch === "(") innerDepth += 1;
    else if (ch === ")") innerDepth = Math.max(0, innerDepth - 1);
    else if (ch === "," && innerDepth === 0) argIndex += 1;
  }

  let receiverName = null;
  let receiverEnd = nameStart - 1;
  while (receiverEnd >= 0 && /\s/.test(text[receiverEnd])) receiverEnd -= 1;
  if (receiverEnd >= 0 && text[receiverEnd] === ".") {
    let receiverStart = receiverEnd - 1;
    while (receiverStart >= 0 && /\s/.test(text[receiverStart])) {
      receiverStart -= 1;
    }
    let tokenStart = receiverStart;
    while (tokenStart >= 0 && /[A-Za-z0-9_]/.test(text[tokenStart])) {
      tokenStart -= 1;
    }
    receiverName = text.slice(tokenStart + 1, receiverStart + 1) || null;
  }

  return { name, argIndex, receiverName };
};


const extractClassDefinitions = (text) => {
  const classes = new Map();
  const codeMask = getCodeMask(text);
  const classRegex = /\bclass\|\s*([A-Za-z_][A-Za-z0-9_]*)(?:\|\s*extends\s*\|\s*([A-Za-z_][A-Za-z0-9_]*))?\s*\{/g;
  let match;

  while ((match = classRegex.exec(text)) !== null) {
    const name = match[1];
    const parent = match[2] || null;
    let braceIndex = text.indexOf("{", match.index);
    while (braceIndex >= 0 && !codeMask[braceIndex]) {
      braceIndex = text.indexOf("{", braceIndex + 1);
    }
    if (braceIndex < 0) continue;

    let depth = 1;
    let i = braceIndex + 1;
    for (; i < text.length; i += 1) {
      if (!codeMask[i]) continue;
      if (text[i] == "{") depth += 1;
      else if (text[i] == "}") {
        depth -= 1;
        if (depth == 0) break;
      }
    }
    if (depth != 0) continue;

    const bodyEnd = i;
    const body = text.slice(braceIndex + 1, bodyEnd);

    const fields = [];
    const methods = [];

    const fieldRegex = /\b(?:(public|private|protected)\s+)?(number|string|bool|component)\|\s*([A-Za-z_][A-Za-z0-9_]*)/g;
    let fieldMatch;
    while ((fieldMatch = fieldRegex.exec(body)) !== null) {
      fields.push({
        name: fieldMatch[3],
        type: fieldMatch[2],
        visibility: fieldMatch[1] || "public",
        ownerClass: name,
      });
    }

    const methodRegex = /\b(?:(public|private|protected)\s+)?function\|\s*(number|string|bool|void)\s*\|\s*([A-Za-z_][A-Za-z0-9_]*)\s*\(([^)]*)\)/g;
    let methodMatch;
    while ((methodMatch = methodRegex.exec(body)) !== null) {
      methods.push({
        name: methodMatch[3],
        returnType: methodMatch[2],
        visibility: methodMatch[1] || "public",
        paramsRaw: methodMatch[4] || "",
        ownerClass: name,
      });
    }

    classes.set(name, {
      name,
      parent,
      fields,
      methods,
      rangeStart: braceIndex,
      rangeEnd: bodyEnd,
    });

    classRegex.lastIndex = bodyEnd + 1;
  }

  return classes;
};

const extractInstanceMap = (text, classMap) => {
  const instances = new Map();
  if (!classMap || !classMap.size) return instances;

  for (const className of classMap.keys()) {
    const regex = new RegExp(`\\b${className}\\s*\\|\\s*([^;]+);`, "g");
    let match;
    while ((match = regex.exec(text)) !== null) {
      const raw = match[1] || "";
      splitTopLevel(raw).forEach((part) => {
        const name = extractDeclaredName(part);
        if (!name) return;
        if (!instances.has(name)) instances.set(name, className);
      });
    }
  }

  return instances;
};

const getEnclosingClassName = (offset, classMap) => {
  if (!classMap || !classMap.size) return null;
  let selected = null;
  for (const [name, def] of classMap.entries()) {
    if (offset > def.rangeStart && offset <= def.rangeEnd) {
      if (!selected || def.rangeStart > selected.rangeStart) {
        selected = { name, rangeStart: def.rangeStart };
      }
    }
  }
  return selected?.name || null;
};


const isSubclass = (classMap, child, ancestor) => {
  if (!child || !ancestor) return false;
  if (child === ancestor) return true;
  let current = child;
  while (current) {
    const def = classMap.get(current);
    if (!def) break;
    current = def.parent || null;
    if (current === ancestor) return true;
  }
  return false;
};

const canAccessMember = (visibility, ownerClass, contextClass, classMap) => {
  if (!visibility || visibility === "public") return true;
  if (!contextClass) return false;
  if (visibility === "private") return contextClass === ownerClass;
  if (visibility === "protected") return isSubclass(classMap, contextClass, ownerClass);
  return false;
};

const getClassMembers = (className, classMap) => {
  const fields = [];
  const methods = [];
  const seenFields = new Set();
  const seenMethods = new Set();
  let current = className;

  while (current) {
    const def = classMap.get(current);
    if (!def) break;

    def.fields.forEach((f) => {
      if (!seenFields.has(f.name)) {
        fields.push(f);
        seenFields.add(f.name);
      }
    });

    def.methods.forEach((m) => {
      if (!seenMethods.has(m.name)) {
        methods.push(m);
        seenMethods.add(m.name);
      }
    });

    current = def.parent || null;
  }

  return { fields, methods };
};

const toImportedClassMap = (classMap) => {
  const importedClasses = new Map();
  if (!classMap?.size) return importedClasses;

  for (const [name, def] of classMap.entries()) {
    importedClasses.set(name, {
      ...def,
      fields: Array.isArray(def.fields)
        ? def.fields.map((field) => ({ ...field }))
        : [],
      methods: Array.isArray(def.methods)
        ? def.methods.map((method) => ({ ...method }))
        : [],
      rangeStart: -1,
      rangeEnd: -1,
    });
  }

  return importedClasses;
};

const buildCombinedClassMap = (text, importedClassMap = null) => {
  const localClassMap = extractClassDefinitions(text);
  const combinedClassMap = new Map();

  if (importedClassMap?.size) {
    for (const [name, def] of importedClassMap.entries()) {
      combinedClassMap.set(name, def);
    }
  }

  for (const [name, def] of localClassMap.entries()) {
    combinedClassMap.set(name, def);
  }

  return combinedClassMap;
};

const resolveMethodSignature = (text, context, importedClassMap = null) => {
  if (!context?.receiverName) return null;

  const classMap = buildCombinedClassMap(text, importedClassMap);
  const instanceMap = extractInstanceMap(text, classMap);
  let className = null;

  if (context.receiverName === "this" || context.receiverName === "super") {
    const enclosing = getEnclosingClassName(context.offset, classMap);
    if (enclosing) {
      className =
        context.receiverName === "super"
          ? classMap.get(enclosing)?.parent || null
          : enclosing;
    }
  } else {
    className = instanceMap.get(context.receiverName) || null;
  }

  if (!className) return null;

  const members = getClassMembers(className, classMap);
  const method = members.methods.find((candidate) => candidate.name === context.name);
  if (!method) return null;

  return {
    name: `${context.receiverName}.${method.name}`,
    returnType: method.returnType || "any",
    params: parseParameterList(method.paramsRaw || ""),
    source: "method",
  };
};

const parseDeclaredComponentNames = (text) => {
  const names = new Set();
  const regex = /\bcomponent\s*\|\s*([^;{}]+)/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const raw = match[1] || "";
    splitTopLevel(raw).forEach((part) => {
      const name = extractDeclaredName(part);
      if (name) names.add(name);
    });
  }

  return Array.from(names);
};

const getDslStatementContext = (text, offset) => {
  const safeOffset = Math.max(0, Math.min(text.length, offset));
  const uptoCursor = text.slice(0, safeOffset);
  const boundary = Math.max(
    uptoCursor.lastIndexOf(";"),
    uptoCursor.lastIndexOf("\n"),
    uptoCursor.lastIndexOf("{"),
    uptoCursor.lastIndexOf("}"),
  );
  const rawStatement = uptoCursor.slice(boundary + 1);
  const statement = rawStatement.trimStart();
  if (!statement) return null;

  const segments = statement.split("|");
  const segmentIndex = Math.max(0, segments.length - 1);
  const command = segments[0]?.trim() || "";
  const commandDef = BUILT_IN_COMMANDS[command] || null;
  const currentSegment = segments[segmentIndex] ?? "";

  return {
    command,
    commandDef,
    segments,
    segmentIndex,
    currentSegment,
  };
};

const parseAssignedProps = (segment) => {
  const assigned = new Set();
  const regex = /([A-Za-z_][A-Za-z0-9_]*)\s*=/g;
  let match;
  while ((match = regex.exec(segment || "")) !== null) {
    assigned.add(match[1]);
  }
  return assigned;
};

const getCurrentPropName = (segment) => {
  const token = String(segment || "").split(",").pop()?.trim() || "";
  if (!token) return "";
  return token.split("=")[0]?.trim() || "";
};

const isEditingPropValue = (segment) => {
  const token = String(segment || "").split(",").pop()?.trim() || "";
  return token.includes("=");
};

const getCurrentPropValueFragment = (segment) => {
  const token = String(segment || "").split(",").pop()?.trim() || "";
  if (!token.includes("=")) return "";
  return token.split("=").slice(1).join("=").trim();
};

const getPropValueSuggestions = (type, monaco, range) => {
  if (type === "string") {
    return [
      {
        label: '"..."',
        kind: monaco.languages.CompletionItemKind.Value,
        detail: "string",
        insertText: '"$1"',
        insertTextRules:
          monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        range,
      },
    ];
  }

  if (type === "bool") {
    return ["true", "false"].map((value) => ({
      label: value,
      kind: monaco.languages.CompletionItemKind.Value,
      detail: "bool",
      insertText: value,
      range,
    }));
  }

  return [
    {
      label: "0",
      kind: monaco.languages.CompletionItemKind.Value,
      detail: type || "value",
      insertText: "0",
      range,
    },
  ];
};

const getDotContext = (text, offset) => {
  let i = offset - 1;
  while (i >= 0 && /\s/.test(text[i])) i -= 1;
  if (i < 0 || text[i] !== ".") return null;

  let j = i - 1;
  while (j >= 0 && /\s/.test(text[j])) j -= 1;
  let end = j;
  while (j >= 0 && /[A-Za-z0-9_]/.test(text[j])) j -= 1;
  const name = text.slice(j + 1, end + 1);
  if (!name) return null;
  return { name };
};

const registerAnimationScriptLanguage = (monaco) => {
  if (monaco.languages.getLanguages().some((l) => l.id === LANGUAGE_ID)) {
    return;
  }

  monaco.languages.register({ id: LANGUAGE_ID });

  monaco.languages.setMonarchTokensProvider(LANGUAGE_ID, {
    defaultToken: "",
    tokenPostfix: ".animation-script",
    ignoreCase: false,

    commands: COMMANDS,
    specialFns: SPECIAL_FNS,

    tokenizer: {
      root: [
        [/\/\/.*$/, "comment"],
        [/\/\*/, "comment", "@blockComment"],
        [/"([^"\\]|\\.)*"/, "string"],
        [/'([^'\\]|\\.)*'/, "string"],
        [/`([^`\\]|\\.)*`/, "string"],
        [/\b(?:number|string|bool|void|component|array)\b/, "type"],
        [
          /\b(?:time|if|else|for|while|function|return|break|continue|true|false|audio|import|class|extends|this|super|public|private|protected)\b/,
          "keyword",
        ],
        [/\b\d+\.?\d*\b/, "number"],
        [/[{}()\[\];,=]/, "delimiter"],
        [/->/, "operator"],
        [/[|]/, "operator"],
        [
          /[A-Za-z_][A-Za-z0-9_]*/,
          {
            cases: {
              "@commands": "command",
              "@specialFns": "special",
              "@default": "identifier",
            },
          },
        ],
        [/\s+/, "white"],
      ],
      blockComment: [
        [/[^*]+/, "comment"],
        [/\*\//, "comment", "@pop"],
        [/[*]/, "comment"],
      ],
    },
  });

  monaco.languages.setLanguageConfiguration(LANGUAGE_ID, {
    comments: {
      lineComment: "//",
      blockComment: ["/*", "*/"],
    },
    brackets: [
      ["{", "}"],
      ["[", "]"],
      ["(", ")"],
    ],
    autoClosingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
      { open: "`", close: "`" },
    ],
    surroundingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
  });
};

const registerCompletionProvider = (
  monaco,
  snippets,
  getExtraFunctions,
  getDslContext,
  getImportedClasses,
) => {
  return monaco.languages.registerCompletionItemProvider(LANGUAGE_ID, {
    triggerCharacters: ["|", ".", "/"],
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const items = [];

      const textValue = model.getValue();
      const offset = model.getOffsetAt(position);
      const importedClassMap =
        typeof getImportedClasses === "function" ? getImportedClasses() : null;
      const classMap = buildCombinedClassMap(textValue, importedClassMap);
      const dotContext = getDotContext(textValue, offset);
      if (dotContext) {
        const instanceMap = extractInstanceMap(textValue, classMap);
        let className = null;

        if (dotContext.name === "this" || dotContext.name === "super") {
          const enclosing = getEnclosingClassName(offset, classMap);
          if (enclosing) {
            if (dotContext.name === "super") {
              className = classMap.get(enclosing)?.parent || null;
            } else {
              className = enclosing;
            }
          }
        } else {
          className = instanceMap.get(dotContext.name) || null;
        }

        if (className) {
          const members = getClassMembers(className, classMap);
          const contextClass = getEnclosingClassName(offset, classMap);
          const suggestions = [];

          const allowMember = (member) => {
            if (dotContext.name === "this") {
              return canAccessMember(member.visibility, member.ownerClass || className, className, classMap);
            }
            if (dotContext.name === "super") {
              if (member.visibility === "private") return false;
              return canAccessMember(member.visibility, member.ownerClass || className, contextClass, classMap);
            }
            return canAccessMember(member.visibility, member.ownerClass || className, contextClass, classMap);
          };

          members.fields.forEach((f) => {
            if (!allowMember(f)) return;
            suggestions.push({
              label: f.name,
              kind: monaco.languages.CompletionItemKind.Field,
              detail: `${f.visibility || "public"} ${f.type}`,
              insertText: f.name,
              range,
            });
          });
          members.methods.forEach((m) => {
            if (!allowMember(m)) return;
            suggestions.push({
              label: m.name,
              kind: monaco.languages.CompletionItemKind.Method,
              detail: `${m.visibility || "public"} function`,
              insertText: `${m.name}(`,
              range,
            });
          });

          return { suggestions };
        }
      }

      const dslContext = getDslStatementContext(textValue, offset);
      const externalDslContext =
        typeof getDslContext === "function" ? getDslContext(textValue) : {};
      const componentNames = Array.from(
        new Set([
          ...(externalDslContext?.componentNames || []),
          ...parseDeclaredComponentNames(textValue),
        ]),
      )
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));

      if (dslContext?.commandDef) {
        const { commandDef, segmentIndex, currentSegment, command, segments } =
          dslContext;

        if (commandDef.allowTargets !== false && segmentIndex === 1) {
          const suggestions = componentNames
            .filter(
              (name) =>
                !word.word ||
                name.toLowerCase().startsWith((word.word || "").toLowerCase()),
            )
            .map((name) => ({
              label: name,
              kind: monaco.languages.CompletionItemKind.Variable,
              detail: `${command} target`,
              insertText: name,
              range,
            }));

          if (suggestions.length) {
            return { suggestions };
          }
        }

        const propsSegmentIndex = commandDef.allowTargets === false ? 1 : 2;
        if (segmentIndex >= propsSegmentIndex && commandDef.validProps) {
          const propsSegment = segments.slice(propsSegmentIndex).join("|");
          const assignedProps = parseAssignedProps(propsSegment);
          const currentPropName = getCurrentPropName(currentSegment);
          const currentValueFragment =
            getCurrentPropValueFragment(currentSegment);

          if (isEditingPropValue(currentSegment)) {
            const propDef = commandDef.validProps[currentPropName];
            if (propDef?.type && !currentValueFragment) {
              return {
                suggestions: getPropValueSuggestions(
                  propDef.type,
                  monaco,
                  range,
                ),
              };
            }
          }

          const propSuggestions = Object.entries(commandDef.validProps)
            .filter(([propName]) => {
              if (!currentPropName && assignedProps.has(propName)) return false;
              if (
                currentPropName &&
                propName !== currentPropName &&
                assignedProps.has(propName)
              ) {
                return false;
              }
              return (
                !word.word ||
                propName.toLowerCase().startsWith((word.word || "").toLowerCase())
              );
            })
            .map(([propName, propDef]) => ({
              label: propName,
              kind: monaco.languages.CompletionItemKind.Property,
              detail: `${command} ${propDef.type || "value"}`,
              insertText:
                propDef.type === "string"
                  ? `${propName}="$1"`
                  : `${propName}=$1`,
              insertTextRules:
                monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              range,
            }));

          if (propSuggestions.length) {
            items.push(...propSuggestions);
          }
        }
      }


      const prefix = (word.word || "").toLowerCase();

      const kindMap = {
        type: monaco.languages.CompletionItemKind.TypeParameter,
        keyword: monaco.languages.CompletionItemKind.Keyword,
        command: monaco.languages.CompletionItemKind.Function,
        special: monaco.languages.CompletionItemKind.Function,
        snippet: monaco.languages.CompletionItemKind.Snippet,
      };

      TYPES.forEach((t) => {
        if (!prefix || t.toLowerCase().startsWith(prefix)) {
          items.push({ label: t, kind: kindMap.type, detail: "type", insertText: t, range });
        }
      });
      Array.from(classMap.keys())
        .sort((a, b) => a.localeCompare(b))
        .forEach((className) => {
          if (!prefix || className.toLowerCase().startsWith(prefix)) {
            items.push({
              label: className,
              kind: monaco.languages.CompletionItemKind.Class,
              detail: importedClassMap?.has(className) ? "imported class" : "class",
              insertText: className,
              range,
            });
          }
        });
      KEYWORDS.forEach((k) => {
        if (!prefix || k.toLowerCase().startsWith(prefix)) {
          items.push({ label: k, kind: kindMap.keyword, detail: "keyword", insertText: k, range });
        }
      });
      COMMANDS.forEach((c) => {
        if (!prefix || c.toLowerCase().startsWith(prefix)) {
          items.push({ label: c, kind: kindMap.command, detail: "command", insertText: c, range });
        }
      });
      SPECIAL_FNS.forEach((f) => {
        if (!prefix || f.toLowerCase().startsWith(prefix)) {
          items.push({ label: f, kind: kindMap.special, detail: "function", insertText: `${f}(`, range });
        }
      });

      const extraFunctions = typeof getExtraFunctions === "function" ? getExtraFunctions() : [];
      extraFunctions.forEach((f) => {
        if (!prefix || f.toLowerCase().startsWith(prefix)) {
          items.push({
            label: f,
            kind: kindMap.special,
            detail: "imported function",
            insertText: `${f}(`,
            range,
          });
        }
      });

      if (Array.isArray(snippets)) {
        snippets.forEach((s) => {
          if (!prefix || (s.name && s.name.toLowerCase().includes(prefix))) {
            items.push({
              label: s.name || "snippet",
              kind: kindMap.snippet,
              detail: s.description || "snippet",
              insertText: (s.code || "").replace(/\/\*[^*]*\*\//g, ""),
              insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              range,
            });
          }
        });
      }

      return { suggestions: items };
    },
  });
};

const registerSignatureHelpProvider = (
  monaco,
  getImportedSignatures,
  getImportedClasses,
) => {
  return monaco.languages.registerSignatureHelpProvider(LANGUAGE_ID, {
    signatureHelpTriggerCharacters: ["(", ","],
    signatureHelpRetriggerCharacters: [","],
    provideSignatureHelp: (model, position) => {
      const text = model.getValue();
      const offset = model.getOffsetAt(position);
      const context = getCallContext(text, offset);
      if (!context) return { value: { signatures: [], activeSignature: 0, activeParameter: 0 }, dispose: () => {} };
      const importedClassMap =
        typeof getImportedClasses === "function" ? getImportedClasses() : null;
      const methodSignature = resolveMethodSignature(
        text,
        { ...context, offset },
        importedClassMap,
      );
      const imported =
        typeof getImportedSignatures === "function" ? getImportedSignatures() : null;

      const signatures = buildFunctionSignatureMap(text);
      if (imported && imported.size) {
        for (const [name, def] of imported.entries()) {
          if (name.includes(".")) continue;
          if (!signatures.has(name)) {
            signatures.set(name, def);
          }
        }
      }
      const signature =
        methodSignature || signatures.get(context.name);
      if (!signature) return { value: { signatures: [], activeSignature: 0, activeParameter: 0 }, dispose: () => {} };

      const params = signature.params || [];
      const label = `${signature.name}(${params.map((p, i) => `${p.type || "any"} ${p.name || `p${i + 1}`}`).join(", ")})`;
      const parameters = params.map((p, i) => ({
        label: `${p.type || "any"} ${p.name || `p${i + 1}`}`,
      }));

      const activeParameter = Math.min(context.argIndex, Math.max(0, params.length - 1));
      return {
        value: {
          signatures: [
            {
              label,
              parameters,
              documentation: `returns ${signature.returnType || "any"}`,
            },
          ],
          activeSignature: 0,
          activeParameter,
        },
        dispose: () => {},
      };
    },
  });
};

function fixFindWidgetCloseButton(editor) {
  try {
    const container = editor.getContainerDomNode?.();
    if (!container) return;
    const findWidget = container.querySelector(".find-widget, .editor-widget.find-widget");
    if (!findWidget) return;
    findWidget.querySelectorAll("[title], [aria-label]").forEach((el) => {
      const title = el.getAttribute("title") || "";
      const aria = el.getAttribute("aria-label") || "";
      if (title) el.removeAttribute("title");
      if (title.includes("Escape") || aria.includes("Escape")) {
        el.setAttribute("aria-label", "Close");
      }
    });
  } catch (_) {}
}

const WORKBENCH_DARK_THEME = {
  base: "vs-dark",
  inherit: true,
  rules: [
    { token: "comment", foreground: "8b949e", fontStyle: "italic" },
    { token: "string", foreground: "a5d6ff" },
    { token: "keyword", foreground: "ff7b72" },
    { token: "type", foreground: "79c0ff" },
    { token: "number", foreground: "79c0ff" },
    { token: "delimiter", foreground: "8b949e" },
    { token: "operator", foreground: "8b949e" },
    { token: "identifier", foreground: "e6edf3" },
    { token: "white", foreground: "e6edf3" },
    { token: "command", foreground: "f0883e" },
    { token: "special", foreground: "a78bfa" },
    { token: "snippet", foreground: "f59e0b" },
  ],
  colors: {
    "editor.background": "#101012",
    "editor.foreground": "#d4d4d8",
    "editor.lineHighlightBackground": "#161618",
    "editorLineNumber.foreground": "#6e7681",
    "editorLineNumber.activeForeground": "#d4d4d8",
    "editorCursor.foreground": "#d4d4d8",
    "editor.selectionBackground": "#264f7866",
    "editor.inactiveSelectionBackground": "#3a3d41",
  },
};

const MonacoScriptEditor = ({
  animation,
  onScriptChange = () => {},
  onExecuted,
  pushLog,
  onPreview,
  animationId,
  onClearLogs,
  logs = [],
  visualSummary = null,
  registerInsertAtCursor = () => {},
}) => {
  const [script, setScript] = useState(DEFAULT_SCRIPT);
  const [scriptError, setScriptError] = useState(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const wrapperRef = useRef(null);
  const completionDisposableRef = useRef(null);
  const signatureDisposableRef = useRef(null);
  const signatureTriggerDisposableRef = useRef(null);
  const containerRef = useRef(null);
  const importedFunctionsRef = useRef(new Map());
  const importedClassesRef = useRef(new Map());
  const importFetchIdRef = useRef(0);
  const importKeyRef = useRef("");
  const executeRef = useRef(() => {});
  const setScriptRef = useRef(() => {});
  const animationRef = useRef(animation);
  const dragRef = useRef(false);
  const dragStartRef = useRef({ y: 0, height: 0 });
  const { allSnippets } = useSnippets();
  const [showConsole, setShowConsole] = useState(true);
  const [consoleHeight, setConsoleHeight] = useState(180);
  const [bottomTab, setBottomTab] = useState("console");
  const [previewSummaryMode, setPreviewSummaryMode] = useState("none");
  const [draftSaveStatus, setDraftSaveStatus] = useState("idle");
  const lastSavedScriptRef = useRef(DEFAULT_SCRIPT);
  const draftTimerRef = useRef(null);
  const isDraftSavingRef = useRef(false);
  const queuedDraftRef = useRef(null);
  const recentEditorValuesRef = useRef([]);

  const {
    isLoadingScript,
    hasPrev,
    hasNext,
    totalVersions,
    currentPage,
    loadVersion,
    resetToDefault,
  } = useScriptVersions(
    animationId,
    pushLog,
    DEFAULT_SCRIPT,
    {
      script,
      setScript,
    },
    {
      onLoadedScriptChange: (loadedScript) => {
        lastSavedScriptRef.current = loadedScript;
        queuedDraftRef.current = null;
        setDraftSaveStatus("idle");
      },
    },
  );

  const { isExecuting, isPreviewing, preview, execute } = useScriptExecution({
    animationId,
    script,
    pushLog,
    clearLogs: onClearLogs,
    onExecuted,
    onExecutedRefresh: () => loadVersion(1),
    onExecutionSuccess: () => setScriptError(null),
    onExecutionError: (error) =>
      setScriptError(
        error?.line != null && error?.char != null
          ? { line: error.line, char: error.char, message: error.message }
          : null,
      ),
    onPreview,
    previewSummaryMode,
  });

  executeRef.current = execute;
  setScriptRef.current = setScript;
  animationRef.current = animation;

  const queueRecentEditorValue = useCallback((nextValue) => {
    const queue = recentEditorValuesRef.current;
    queue.push(String(nextValue ?? ""));
    if (queue.length > RECENT_EDITOR_VALUE_QUEUE_LIMIT) {
      queue.splice(0, queue.length - RECENT_EDITOR_VALUE_QUEUE_LIMIT);
    }
  }, []);

  const consumeQueuedEditorValue = useCallback((nextValue) => {
    const queue = recentEditorValuesRef.current;
    const matchIndex = queue.indexOf(String(nextValue ?? ""));
    if (matchIndex === -1) {
      return false;
    }
    queue.splice(0, matchIndex + 1);
    return true;
  }, []);

  const syncEditorModelValue = useCallback((nextValue) => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco) return false;

    const model = editor.getModel();
    if (!model) return false;

    const normalizedValue = String(nextValue ?? "");
    if (model.getValue() === normalizedValue) {
      return true;
    }

    const previousSelections = editor.getSelections();

    editor.executeEdits("external-script-sync", [
      {
        range: model.getFullModelRange(),
        text: normalizedValue,
        forceMoveMarkers: true,
      },
    ]);
    editor.pushUndoStop();

    if (previousSelections?.length) {
      const safeSelections = previousSelections.map((selection) => {
        const start = model.validatePosition(selection.getStartPosition());
        const end = model.validatePosition(selection.getEndPosition());
        return new monaco.Selection(
          start.lineNumber,
          start.column,
          end.lineNumber,
          end.column,
        );
      });
      editor.setSelections(safeSelections);
    }

    return true;
  }, []);

  const persistDraft = useCallback(
    async (scriptText) => {
      if (!animationId || typeof scriptText !== "string") return false;
      if (isDraftSavingRef.current) {
        queuedDraftRef.current = scriptText;
        return false;
      }
      if (scriptText === lastSavedScriptRef.current) {
        setDraftSaveStatus("saved");
        return true;
      }

      isDraftSavingRef.current = true;
      setDraftSaveStatus("saving");

      try {
        await saveScriptDraft(animationId, scriptText);
        lastSavedScriptRef.current = scriptText;
        setDraftSaveStatus("saved");
        return true;
      } catch (error) {
        setDraftSaveStatus("error");
        pushLog("error", error.message || "Failed to save draft");
        return false;
      } finally {
        isDraftSavingRef.current = false;

        const queuedScript = queuedDraftRef.current;
        if (
          typeof queuedScript === "string" &&
          queuedScript !== lastSavedScriptRef.current
        ) {
          queuedDraftRef.current = null;
          persistDraft(queuedScript);
        }
      }
    },
    [animationId, pushLog],
  );

  useEffect(() => {
    if (!animationId) {
      lastSavedScriptRef.current = DEFAULT_SCRIPT;
      queuedDraftRef.current = null;
      setDraftSaveStatus("idle");
      return;
    }
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    if (typeof script !== "string" || script === lastSavedScriptRef.current) {
      return;
    }

    setDraftSaveStatus("saving");
    draftTimerRef.current = window.setTimeout(() => {
      draftTimerRef.current = null;
      persistDraft(script);
    }, DRAFT_AUTOSAVE_DELAY_MS);

    return () => {
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
    };
  }, [animationId, persistDraft, script]);

  useEffect(() => {
    const importNames = parseImportNames(script);
    const importKey = importNames.slice().sort().join("|");

    if (importKeyRef.current === importKey) {
      return;
    }

    importKeyRef.current = importKey;
    importedFunctionsRef.current = new Map();
    importedClassesRef.current = new Map();

    if (!importNames.length) {
      return;
    }

    const fetchId = ++importFetchIdRef.current;
    let cancelled = false;

    const loadImportedSymbols = async () => {
      const nextFunctions = new Map();
      const nextClasses = new Map();

      await Promise.all(
        importNames.map(async (importName) => {
          try {
            const searchResult = await getAnimations({
              search: importName,
              limit: 25,
              page: 1,
            });
            const matchedAnimation = findImportedAnimationMatch(
              searchResult?.records || [],
              importName,
            );
            const matchedAnimationId = getAnimationLookupId(matchedAnimation);
            if (!matchedAnimationId) return;

            const importedScriptRecord = await fetchScript(matchedAnimationId);
            const importedScriptText = getScriptText(importedScriptRecord);
            if (!importedScriptText.trim()) return;

            for (const [name, def] of extractDeclaredFunctions(
              importedScriptText,
            ).entries()) {
              if (!nextFunctions.has(name)) {
                nextFunctions.set(name, {
                  ...def,
                  source: "imported",
                });
              }
            }

            for (const [name, def] of toImportedClassMap(
              extractClassDefinitions(importedScriptText),
            ).entries()) {
              if (!nextClasses.has(name)) {
                nextClasses.set(name, def);
              }
            }
          } catch (_) {
            // Keep the editor responsive even if an imported script cannot be resolved.
          }
        }),
      );

      if (cancelled || fetchId !== importFetchIdRef.current) {
        return;
      }

      importedFunctionsRef.current = nextFunctions;
      importedClassesRef.current = nextClasses;
    };

    loadImportedSymbols();

    return () => {
      cancelled = true;
    };
  }, [script]);

  const handleBeforeMount = useCallback((monaco) => {
    monaco.editor.defineTheme("workbench-script", WORKBENCH_DARK_THEME);
    registerAnimationScriptLanguage(monaco);
  }, []);

  const handleEditorMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;
      setIsEditorReady(true);

      monaco.editor.setTheme("workbench-script");

      completionDisposableRef.current = registerCompletionProvider(
        monaco,
        allSnippets,
        () =>
          Array.from(importedFunctionsRef.current.keys()).filter(
            (name) => !name.includes("."),
          ),
        (textValue) => ({
          componentNames: Array.from(
            new Set([
              ...((animationRef.current?.animationComponents || []).map(
                (item) => item.animationComponentName,
              )),
              ...parseDeclaredComponentNames(textValue),
            ]),
          ).filter(Boolean),
        }),
        () => importedClassesRef.current,
      );
      signatureDisposableRef.current = registerSignatureHelpProvider(
        monaco,
        () => importedFunctionsRef.current,
        () => importedClassesRef.current,
      );

      signatureTriggerDisposableRef.current = editor.onDidChangeCursorPosition(() => {
        const model = editor.getModel();
        if (!model) return;
        const offset = model.getOffsetAt(editor.getPosition());
        const value = model.getValue();
        const context = getCallContext(value, offset);
        if (!context) return;
        editor.trigger("keyboard", "editor.action.triggerParameterHints", null);
      });

      editor.onDidChangeCursorSelection(() => {
        const model = editor.getModel();
        if (!model) return;
        const offset = model.getOffsetAt(editor.getPosition());
        const value = model.getValue();
        const context = getCallContext(value, offset);
        if (!context) return;
        editor.trigger("mouse", "editor.action.triggerParameterHints", null);
      });

      editor.onMouseDown(() => {
        const model = editor.getModel();
        if (!model) return;
        const offset = model.getOffsetAt(editor.getPosition());
        const value = model.getValue();
        const context = getCallContext(value, offset);
        if (!context) return;
        editor.trigger("mouse", "editor.action.triggerParameterHints", null);
      });

      editor.addAction({
        id: "run-script",
        label: "Run script",
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
        run: () => executeRef.current?.(),
      });

      editor.addAction({
        id: "format-script",
        label: "Format script",
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF],
        run: (ed) => {
          const value = ed.getModel()?.getValue() ?? "";
          const formatted = formatAnimationScript(value);
          if (formatted !== value) setScriptRef.current?.(formatted);
        },
      });

      editor.addAction({
        id: "find-script",
        label: "Find",
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF],
        run: (ed) => {
          ed.trigger("keyboard", "editor.action.startFindAction", null);
          setTimeout(() => fixFindWidgetCloseButton(ed), 80);
        },
      });

      editor.addAction({
        id: "replace-script",
        label: "Replace",
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyH],
        run: (ed) => {
          ed.trigger("keyboard", "editor.action.startFindReplaceAction", null);
          setTimeout(() => fixFindWidgetCloseButton(ed), 80);
        },
      });
    },
    [allSnippets],
  );

  useEffect(() => {
    registerInsertAtCursor((nextText = "") => {
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      if (!editor || !monaco) return false;

      const model = editor.getModel();
      if (!model) return false;

      const currentPosition = editor.getPosition();
      const selection = editor.getSelection();
      const range =
        selection ||
        new monaco.Range(
          currentPosition?.lineNumber || 1,
          currentPosition?.column || 1,
          currentPosition?.lineNumber || 1,
          currentPosition?.column || 1,
        );

      editor.executeEdits("marker-time-insert", [
        {
          range,
          text: String(nextText),
          forceMoveMarkers: true,
        },
      ]);
      editor.focus();

      const nextSelection = editor.getSelection();
      if (nextSelection) {
        editor.revealPositionInCenterIfOutsideViewport(
          nextSelection.getEndPosition(),
        );
      }

      return true;
    });

    return () => {
      registerInsertAtCursor(null);
    };
  }, [registerInsertAtCursor]);

  // useEffect(() => {
  //   const onKeyDown = (e) => {
  //     const wrapper = wrapperRef.current;
  //     if (!wrapper || !document.body.contains(wrapper) || !wrapper.offsetParent) return;
  //     const isCtrl = e.ctrlKey || e.metaKey;
  //     if (!isCtrl || (e.key !== "f" && e.key !== "F")) return;
  //     const focusInside = wrapper.contains(document.activeElement);
  //     if (!focusInside) return;
  //     e.preventDefault();
  //     e.stopPropagation();
  //     const ed = editorRef.current;
  //     if (!ed) return;
  //     if (e.shiftKey) {
  //       const value = ed.getModel()?.getValue() ?? "";
  //       const formatted = formatAnimationScript(value);
  //       if (formatted !== value) setScriptRef.current?.(formatted);
  //       return;
  //     }
  //     ed.focus();
  //     ed.trigger("keyboard", "editor.action.startFindAction", null);
  //     setTimeout(() => fixFindWidgetCloseButton(ed), 80);
  //     setTimeout(() => fixFindWidgetCloseButton(ed), 250);
  //   };
  //   document.addEventListener("keydown", onKeyDown, true);
  //   return () => document.removeEventListener("keydown", onKeyDown, true);
  // }, []);

  useEffect(() => {
    return () => {
      completionDisposableRef.current?.dispose();
      signatureDisposableRef.current?.dispose();
      signatureTriggerDisposableRef.current?.dispose();
    };
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco) return;

    const model = editor.getModel();
    if (!model) return;

    if (scriptError?.line != null && scriptError?.char != null) {
      monaco.editor.setModelMarkers(model, "script-error", [
        {
          severity: monaco.MarkerSeverity.Error,
          message: scriptError.message || "Script error",
          startLineNumber: scriptError.line,
          startColumn: scriptError.char,
          endLineNumber: scriptError.line,
          endColumn: scriptError.char + 1,
        },
      ]);
      const pos = { lineNumber: scriptError.line, column: scriptError.char };
      editor.setPosition(pos);
      editor.revealPositionInCenterIfOutsideViewport(pos);
      editor.focus();
    } else {
      monaco.editor.setModelMarkers(model, "script-error", []);
    }
  }, [scriptError]);

  const handleChange = useCallback((value) => {
    const nextValue = value ?? "";
    queueRecentEditorValue(nextValue);
    setScript(nextValue);
    if (scriptError) setScriptError(null);
  }, [queueRecentEditorValue, scriptError]);

  useEffect(() => {
    if (!isEditorReady) return;
    if (consumeQueuedEditorValue(script)) {
      return;
    }
    syncEditorModelValue(script);
  }, [consumeQueuedEditorValue, isEditorReady, script, syncEditorModelValue]);

  useEffect(() => {
    onScriptChange(script);
  }, [onScriptChange, script]);

const openFind = useCallback(() => {
  const editor = editorRef.current;
  if (!editor) return;
  editor.focus();
  editor.trigger("keyboard", "actions.find", null);
  setTimeout(() => fixFindWidgetCloseButton(editor), 80);
}, []);

  const formatScript = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const value = editor.getModel()?.getValue() ?? "";
    const formatted = formatAnimationScript(value);
    if (formatted !== value) {
      setScriptRef.current?.(formatted);
    }
    editor.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      const wrapper = wrapperRef.current;
      if (!wrapper || !document.body.contains(wrapper) || !wrapper.offsetParent) {
        return;
      }
      const isCtrl = event.ctrlKey || event.metaKey;
      if (!isCtrl) return;
      const key = event.key?.toLowerCase();
      if ((key !== "f" && key !== "h") || event.shiftKey) return;
      if (!wrapper.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      const editor = editorRef.current;
      if (!editor) return;
      if (key === "h") {
        editor.trigger("keyboard", "editor.action.startFindReplaceAction", null);
        setTimeout(() => fixFindWidgetCloseButton(editor), 80);
        return;
      }
      openFind();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [openFind]);

  const toggleConsole = useCallback(() => {
    setShowConsole((v) => !v);
  }, []);

  useEffect(() => {
    const handleMove = (event) => {
      if (!dragRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const maxHeight = Math.max(
        MIN_CONSOLE_HEIGHT,
        rect.height - MIN_EDITOR_HEIGHT - SPLITTER_SIZE,
      );
      const delta = dragStartRef.current.y - event.clientY;
      const next = Math.max(
        MIN_CONSOLE_HEIGHT,
        Math.min(maxHeight, dragStartRef.current.height + delta),
      );
      setConsoleHeight(next);
    };
    const handleUp = () => {
      dragRef.current = false;
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  const handleSplitterMouseDown = useCallback((event) => {
    dragRef.current = true;
    dragStartRef.current = { y: event.clientY, height: consoleHeight };
    event.preventDefault();
  }, [consoleHeight]);

  return (
    <section className="animation-workbench-script">
        <ScriptEditorToolbar
        isExecuting={isExecuting}
        isPreviewing={isPreviewing}
        draftSaveStatus={draftSaveStatus}
        isLoadingScript={isLoadingScript}
        totalVersions={totalVersions}
        currentPage={currentPage}
        hasPrev={hasPrev}
        hasNext={hasNext}
          onLoadOlder={() => loadVersion(currentPage + 1)}
          onLoadNewer={() => loadVersion(currentPage - 1)}
          onFormat={formatScript}
          onOpenFind={openFind}
          onReset={resetToDefault}
        onPreview={preview}
        previewSummaryMode={previewSummaryMode}
        onChangePreviewSummaryMode={setPreviewSummaryMode}
        onRun={execute}
        showConsole={showConsole}
        onToggleConsole={toggleConsole}
      />
      <div className="animation-workbench-script-body" ref={containerRef}>
        <div
          ref={wrapperRef}
          className="animation-workbench-script-monaco-wrapper"
          tabIndex={-1}
          style={{
            minHeight: showConsole ? `${MIN_EDITOR_HEIGHT}px` : "100%",
            height: showConsole
              ? `calc(100% - ${consoleHeight + SPLITTER_SIZE}px)`
              : "100%",
          }}
        >
          <Editor
            height="100%"
            language={LANGUAGE_ID}
            defaultValue={script}
            onChange={handleChange}
            beforeMount={handleBeforeMount}
            onMount={handleEditorMount}
            loading={null}
            options={{
              minimap: { enabled: false },
              fontFamily:
                "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
              fontLigatures: true,
              fontWeight: "400",
              fontSize: 14,
              lineHeight: 21,
              lineNumbers: "on",
              lineNumbersMinChars: 3,
              scrollBeyondLastLine: false,
              folding: true,
              bracketPairColorization: { enabled: true },
              automaticLayout: true,
              tabSize: 5,
              wordWrap: "off",
              readOnly: isExecuting,
              padding: { top: 14, bottom: 14 },
              parameterHints: { enabled: true },
              scrollbar: {
                verticalScrollbarSize: 10,
                horizontalScrollbarSize: 10,
              },
              renderLineHighlight: "line",
              overviewRulerLanes: 0,
            }}
            theme="workbench-script"
          />
        </div>

        {showConsole && (
          <>
            <div
              className="animation-workbench-splitter"
              onMouseDown={handleSplitterMouseDown}
              role="presentation"
            />
            <div
              className="animation-workbench-console-embedded"
              style={{ height: `${consoleHeight}px` }}
            >
              <div className="animation-workbench-bottom-tabs">
                <button
                  type="button"
                  className={`animation-workbench-bottom-tab${
                    bottomTab === "console" ? " active" : ""
                  }`}
                  onClick={() => setBottomTab("console")}
                >
                  Console
                </button>
                <button
                  type="button"
                  className={`animation-workbench-bottom-tab${
                    bottomTab === "summary" ? " active" : ""
                  }`}
                  onClick={() => setBottomTab("summary")}
                >
                  Summary
                </button>
              </div>
              <div className="animation-workbench-bottom-panel-body">
                {bottomTab === "console" ? (
                  <Console logs={logs} onClear={onClearLogs} />
                ) : (
                  <VisualSummaryPanel summary={visualSummary} compact />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default MonacoScriptEditor;
